import { from, ObservableInput, of, pipe, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { z } from 'zod/v4';

import { NormalizedResponse } from '../models/normalized-response';
import { StatusCode } from '../models/status-code';
import { extractErrorCode } from './extract-error-code';

function safeStringify(value: unknown) {
  try {
    return JSON.stringify(value);
  } catch (x) {
    return '';
  }
}

function normalizeResponseObject(
  error: Response,
  errorObject?: Record<string, unknown> | unknown[]
) {
  return Object.assign(error, {
    error: errorObject ?? {},
    errorCode: errorObject ? extractErrorCode(errorObject) : -1,
  }) as NormalizedResponse;
}

/**
 * Map errors to the NormalizedResponse type to simplify error handling downstream.
 *
 * This one is for AFTER getting the response body.
 *
 */
export function normalizeErrorReporting<T>() {
  return pipe(
    catchError<T, ObservableInput<T>>((error) => {
      if (error instanceof Response) {
        return from(
          error.json() as Promise<Record<string, unknown> | unknown[]>
        ).pipe(
          catchError((error) => {
            if (error instanceof SyntaxError) {
              return of({} as Record<string, unknown>);
            }
            throw error;
          }),
          switchMap((errorObject) => {
            return throwError(() =>
              normalizeResponseObject(error, errorObject)
            );
          })
        );
      }

      if (error instanceof z.ZodError) {
        console.error(error.format());

        return throwError(() =>
          normalizeResponseObject(
            new Response(safeStringify(error.format()), {
              status: StatusCode.SchemaParseError,
              statusText: 'Schema Parse Error',
            })
          )
        );
      }

      if (error instanceof ReferenceError) {
        return throwError(() => error);
      }

      return throwError(() =>
        normalizeResponseObject(
          new Response(null, {
            status: StatusCode.UnknownError,
            statusText: 'Unknown Error',
          })
        )
      );
    })
  );
}
